/**
 * useAutoEncryptionMigration — Passive Encryption Migration Hook
 *
 * Automatically migrates all of a doctor's cases from old field-level RSA
 * encryption (ENC:/HENC:) to envelope encryption (CASEKEY:), and grants
 * attorney collaborators access to case key envelopes.
 *
 * Runs silently in the background once per session when encryption is unlocked.
 * No user interaction required — the doctor just uses the app normally.
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { apiClient } from '../api';
import { useEncryption } from '../contexts/EncryptionContext';
import { needsReEncryption, needsReEncryptionIntakeRequest } from '../services/caseKeyService';
import {
  recordEncryptionFailure,
  summarizeEncryptionFailures,
} from '../utils/encryptionFailureLog';

/**
 * sessionStorage key flag that marks "migration already ran this session".
 * Versioned — bump the suffix when migration logic changes to force re-run.
 *
 * Exported so tests can import the canonical value instead of hardcoding the
 * string literal (which silently drifted from `encryption_migration_done` to
 * `_v6` and caused the BatchEncryptionMigration test suite to go red).
 */
export const SESSION_KEY = 'encryption_migration_v6';

export function useAutoEncryptionMigration() {
  const {
    isUnlocked,
    status: encryptionStatus,
    getCaseKey,
    createCaseEnvelope,
    grantCaseAccess,
    reEncryptCaseFields,
    reEncryptIntakeRequestFields,
    decryptField,
  } = useEncryption();

  const runningRef = useRef(false);

  useEffect(() => {
    if (!isUnlocked || !encryptionStatus?.setup) {
      return;
    }
    if (runningRef.current) {
      return;
    }

    // Only run once per session
    if (sessionStorage.getItem(SESSION_KEY)) {
      return;
    }
    runningRef.current = true;

    (async () => {
      try {
        const cases = await apiClient.getCases();

        // Filter: any case that has encryptedPayload OR any field with ENC:/HENC:/CASEKEY: prefix
        const ENCRYPTED_PREFIXES = ['ENC:', 'HENC:', 'CASEKEY:'];
        const SENSITIVE_FIELDS = [
          'caseTitle',
          'plaintiff',
          'attorneyName',
          'attorneyEmail',
          'firmName',
          'description',
          'attorneyPhone',
        ];
        const encryptedCases = cases.filter(
          (c: any) =>
            c.encryptedPayload ||
            SENSITIVE_FIELDS.some(
              (f) => typeof c[f] === 'string' && ENCRYPTED_PREFIXES.some((p) => c[f].startsWith(p)),
            ),
        );
        if (encryptedCases.length === 0) {
          sessionStorage.setItem(SESSION_KEY, '1');
          runningRef.current = false;
          return;
        }

        let reEncrypted = 0;
        let attorneyGranted = 0;
        let errors = 0;

        // Track cases where attorney grant failed (couldn't decrypt email)
        const ungrantedCaseIds = new Set<string>();
        // Track cases where attorney was successfully granted (avoid re-granting in Pass 3)
        const pass1GrantedCases = new Set<string>();
        // Track discovered attorney IDs from successful grants
        const discoveredAttorneys: { id: string; hasPublicKey: boolean }[] = [];

        // Local caches — scoped to this migration run, GC'd when done
        const emailCache = new Map<string, { id: string; hasPublicKey: boolean } | null>();
        const publicKeyCache = new Map<string, string>(); // attorneyId → publicKeyPem

        for (const caseItem of encryptedCases) {
          const caseId = caseItem.id;

          try {
            // Step 1: Ensure envelope exists
            let caseKey = await getCaseKey(caseId);
            if (!caseKey) {
              caseKey = await createCaseEnvelope(caseId);
            }

            // Step 2: Fetch full case data and re-encrypt if needed
            const rawData = await apiClient.getCaseById(caseId);

            if (needsReEncryption(rawData as any)) {
              try {
                const { reEncryptedFields, failedFields } = await reEncryptCaseFields(
                  caseId,
                  rawData as any,
                );
                if (Object.keys(reEncryptedFields).length > 0) {
                  await apiClient.reEncryptCase(caseId, reEncryptedFields);
                  reEncrypted++;
                }
                if (failedFields.length > 0) {
                  recordEncryptionFailure('case', caseId, failedFields);
                }
              } catch (reEncErr) {
                console.warn(`[AutoMigration] Re-encryption failed for case ${caseId}:`, reEncErr);
              }
            }

            // Step 3: Grant attorney access
            let attorneyEmail = (rawData as any).attorneyEmail;
            if (
              attorneyEmail &&
              typeof attorneyEmail === 'string' &&
              (attorneyEmail.startsWith('ENC:') ||
                attorneyEmail.startsWith('HENC:') ||
                attorneyEmail.startsWith('CASEKEY:'))
            ) {
              try {
                const decrypted = await decryptField(attorneyEmail, caseId);
                if (decrypted && !decrypted.startsWith('[')) {
                  attorneyEmail = decrypted;
                }
              } catch {
                attorneyEmail = null;
              }
            }

            if (
              attorneyEmail &&
              typeof attorneyEmail === 'string' &&
              !attorneyEmail.startsWith('ENC:') &&
              !attorneyEmail.startsWith('HENC:') &&
              !attorneyEmail.startsWith('CASEKEY:') &&
              !attorneyEmail.startsWith('[') &&
              attorneyEmail.includes('@')
            ) {
              try {
                // Silent: this whole branch is best-effort (failures
                // skip granting for this case, not fatal to migration).
                // Marking the API calls silent avoids polluting Sentry
                // with the rate-limit / 404 noise that the catch below
                // already handles gracefully.
                if (!emailCache.has(attorneyEmail)) {
                  emailCache.set(
                    attorneyEmail,
                    await apiClient.findUserByEmail(attorneyEmail, { silent: true }),
                  );
                }
                const attorney = emailCache.get(attorneyEmail);
                if (attorney?.hasPublicKey) {
                  if (!publicKeyCache.has(attorney.id)) {
                    const { publicKey } = await apiClient.getUserPublicKey(attorney.id, {
                      silent: true,
                    });
                    if (publicKey) publicKeyCache.set(attorney.id, publicKey);
                  }
                  const cachedKey = publicKeyCache.get(attorney.id);
                  if (cachedKey) {
                    await grantCaseAccess(caseId, attorney.id, cachedKey);
                    attorneyGranted++;
                    pass1GrantedCases.add(caseId);
                    discoveredAttorneys.push(attorney);
                  }
                }
              } catch {
                // Non-fatal
              }
            } else {
              // Attorney email couldn't be decrypted — track for fallback pass
              ungrantedCaseIds.add(caseId);
            }

            // Delay between cases (reduced from 2s — Clerk calls now cached locally)
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (err) {
            errors++;
            console.warn(`[AutoMigration] Failed to process case ${caseId}:`, err);
            // Extra backoff on error (likely rate limit)
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
        }

        console.log(
          `[AutoMigration] Cases: ${encryptedCases.length} processed, ` +
            `${reEncrypted} re-encrypted, ${attorneyGranted} attorney grants, ${errors} errors`,
        );

        // === Pass 2: Intake Request Re-Encryption + Attorney Grants ===
        let intakeReEncrypted = 0;
        let intakeAttorneyGranted = 0;
        let intakeSkipped = 0;
        let intakeErrors = 0;
        // Hoisted for Pass 3 fallback access
        const grantedCases = new Set<string>();
        let intakeWithCase: any[] = [];
        try {
          const intakeRequests = await apiClient.getPreScreeningRequests();
          intakeWithCase = (intakeRequests as any[]).filter((req) => req.createdCaseId);

          for (const req of intakeWithCase) {
            try {
              const caseId = req.createdCaseId;

              // getCaseKey is cache-aware — only hits API on first call per caseId
              let caseKey = await getCaseKey(caseId);
              if (!caseKey) {
                try {
                  caseKey = await createCaseEnvelope(caseId);
                } catch {
                  continue;
                }
              }

              // Re-encrypt if needed
              let reEncryptedFields: Record<string, string> = {};
              if (needsReEncryptionIntakeRequest(req)) {
                const result = await reEncryptIntakeRequestFields(caseId, req);
                reEncryptedFields = result.reEncryptedFields;
                if (Object.keys(reEncryptedFields).length > 0) {
                  await apiClient.reEncryptIntakeRequest(req.id, caseId, reEncryptedFields);
                  intakeReEncrypted++;
                }
                if (result.failedFields.length > 0) {
                  recordEncryptionFailure('intakeRequest', req.id, result.failedFields);
                }
              }

              // Grant attorney access — skip if already granted for this case
              if (grantedCases.has(caseId)) {
                intakeSkipped++;
              } else {
                const emailValue = reEncryptedFields.attorneyEmail || req.attorneyEmail;
                if (emailValue && typeof emailValue === 'string' && !emailValue.startsWith('[')) {
                  try {
                    const email = await decryptField(emailValue, caseId);
                    if (email && !email.startsWith('[') && email.includes('@')) {
                      if (!emailCache.has(email)) {
                        emailCache.set(email, await apiClient.findUserByEmail(email));
                      }
                      const attorney = emailCache.get(email);
                      if (attorney?.hasPublicKey) {
                        if (!publicKeyCache.has(attorney.id)) {
                          const { publicKey } = await apiClient.getUserPublicKey(attorney.id);
                          if (publicKey) publicKeyCache.set(attorney.id, publicKey);
                        }
                        const cachedKey = publicKeyCache.get(attorney.id);
                        if (cachedKey) {
                          await grantCaseAccess(caseId, attorney.id, cachedKey);
                          grantedCases.add(caseId);
                          intakeAttorneyGranted++;
                        }
                      }
                    }
                  } catch {
                    // Non-fatal — attorney grant is best-effort
                  }
                }
              }

              // Delay between intake requests (reduced — Clerk calls now cached locally)
              await new Promise((resolve) => setTimeout(resolve, 500));
            } catch (err) {
              intakeErrors++;
              console.warn(`[AutoMigration] Failed to process intake request ${req.id}:`, err);
              // Extra backoff on error (likely 429)
              await new Promise((resolve) => setTimeout(resolve, 5000));
            }
          }

          console.log(
            `[AutoMigration] Intake requests: ${intakeWithCase.length} with case, ` +
              `${intakeReEncrypted} re-encrypted, ${intakeAttorneyGranted} attorney grants, ` +
              `${intakeSkipped} skipped, ${intakeErrors} errors`,
          );
        } catch (intakeErr) {
          console.warn('[AutoMigration] Failed to fetch intake requests:', intakeErr);
        }

        // === Pass 3: Fallback — grant discovered attorneys to ungranted cases ===
        // When attorneyEmail can't be decrypted (wrong RSA key), we use attorneys
        // discovered from successful grants in Pass 1/2 to cover the remaining cases.
        let fallbackGranted = 0;

        // Merge attorneys discovered from email cache into the list
        for (const [, attorney] of emailCache) {
          if (attorney?.hasPublicKey && !discoveredAttorneys.some((a) => a.id === attorney.id)) {
            discoveredAttorneys.push(attorney);
          }
        }

        // Also add ungranted cases from Pass 2 (intake requests where grant failed)
        for (const req of intakeWithCase) {
          const caseId = req.createdCaseId;
          if (caseId && !grantedCases.has(caseId) && !pass1GrantedCases.has(caseId)) {
            ungrantedCaseIds.add(caseId);
          }
        }

        // Deduplicate attorneys by ID
        const uniqueAttorneys = Array.from(
          new Map(discoveredAttorneys.map((a) => [a.id, a])).values(),
        );

        const ungrantedList = Array.from(ungrantedCaseIds);
        if (ungrantedList.length > 0 && uniqueAttorneys.length > 0) {
          console.log(
            `[AutoMigration] Pass 3: ${ungrantedList.length} ungranted cases, ` +
              `${uniqueAttorneys.length} discovered attorneys — attempting fallback grants`,
          );

          for (const caseId of ungrantedList) {
            for (const attorney of uniqueAttorneys) {
              try {
                const cachedKey = publicKeyCache.get(attorney.id);
                await grantCaseAccess(caseId, attorney.id, cachedKey);
                fallbackGranted++;
              } catch {
                // May fail if already granted or no envelope — non-fatal
              }
            }
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          console.log(`[AutoMigration] Pass 3: ${fallbackGranted} fallback grants`);
        }

        // Only mark done if at least one item succeeded — allows retry on total failure
        const totalItems = encryptedCases.length;
        const totalErrors = errors;
        if (totalErrors < totalItems || intakeReEncrypted > 0 || fallbackGranted > 0) {
          sessionStorage.setItem(SESSION_KEY, '1');
        }

        // Surface fields that couldn't be re-encrypted. Prior to 2026-05-14
        // these were silently dropped; the doctor saw `[Decryption failed]`
        // forever with no recovery path. Now we toast once per session so
        // the user knows to open the affected records and re-enter.
        const { recordCount, fieldCount } = summarizeEncryptionFailures();
        if (recordCount > 0) {
          toast.warning(
            `${fieldCount} encrypted field${fieldCount === 1 ? '' : 's'} on ` +
              `${recordCount} record${recordCount === 1 ? '' : 's'} couldn't be ` +
              'recovered automatically. Open the affected case/intake to re-enter.',
            { duration: 10000 },
          );
        }
      } catch (err) {
        // getCases() or other fatal error — do NOT set session flag so we retry
        console.warn('[AutoMigration] Failed to run migration:', err);
      }

      runningRef.current = false;
    })();
  }, [
    isUnlocked,
    encryptionStatus,
    getCaseKey,
    createCaseEnvelope,
    grantCaseAccess,
    reEncryptCaseFields,
    reEncryptIntakeRequestFields,
    decryptField,
  ]);
}
